import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useDistributionWaterfall,
  type WaterfallViewType,
} from '@/hooks/react-query/useDistributionWaterfall';
import { WaterfallContent } from './WaterfallContent';
import { WaterfallViewOption } from './WaterfallViewOption';
import { APIError } from '@/utils/api-client';

interface FundWaterfallCardProps {
  fundId: string;
  currency?: string;
}

export function FundWaterfallCard({
  fundId,
  currency = 'EUR',
}: FundWaterfallCardProps) {
  const [viewType, setViewType] = useState<WaterfallViewType>('unrealised');
  const { data: result, error } = useDistributionWaterfall(fundId, viewType);

  // A 404 means the fund simply has no waterfall to compute yet (no NAV /
  // distributions), not a failure - show the empty state, not an error.
  const isNotFound = error instanceof APIError && error.status === 404;
  const hasError = !!error && !isNotFound;
  const emptyDataLabel = viewType === 'realised' ? 'distributions' : 'NAV';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-3">
          <div>
            <CardTitle>Distribution Waterfall</CardTitle>
          </div>
        </div>
        <Select
          value={viewType}
          onValueChange={(val: WaterfallViewType) => setViewType(val)}
        >
          <SelectTrigger className="w-[240px] h-8 text-xs bg-muted/50 border-0 outline-none focus:ring-opacity-50 shadow-none font-medium text-muted-foreground hover:bg-muted/80 transition-colors focus:ring-0">
            <SelectValue placeholder="View Type" />
          </SelectTrigger>
          <SelectContent
            align="end"
            className="p-1 border-border/50 shadow-sm rounded-xl animate-in fade-in-80 slide-in-from-top-1"
          >
            <SelectItem
              value="unrealised"
              className="text-xs font-medium cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary transition-colors"
            >
              <WaterfallViewOption
                title="Total Value (NAV + Dist.)"
                description="Current NAV plus all historic distributions"
              />
            </SelectItem>
            <SelectItem
              value="realised"
              className="text-xs font-medium cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary transition-colors"
            >
              <WaterfallViewOption
                title="Realized (Cash)"
                description="Based strictly on historic distributions"
              />
            </SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-6 pt-4">
        {hasError || !result ? (
          <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
            {hasError
              ? 'Error loading waterfall data.'
              : `No ${emptyDataLabel} data available yet.`}
          </div>
        ) : (
          <WaterfallContent
            result={result}
            currency={currency}
            viewType={viewType}
          />
        )}
      </CardContent>
    </Card>
  );
}
